/* SSi website – Intensive Learning HUB (Direction 2).
 *
 * Curatorial spine. Short. Pushes the reader into one of five
 * full-fat sub-stories – three sprints and two marathons – and into the course.
 *
 * Sections:
 *   IH_Hero      – manifesto
 *   IH_Shapes    – the two shapes of intensive (compact)
 *   IH_Sprints   – three large story cards: Japanuary / Ireland / 5-day Welsh
 *   IH_Marathons – two large story cards: Aran's 75 days / Tom's 30 days
 *   IH_Advice    – four-rule postcard (the rest lives on sub-pages or methodology)
 *   IH_CTA       – start a course
 */

import React from 'react';
import { Link } from 'react-router-dom';

function IH_Hero() {
  return (
    <section style={ihStyles.heroWrap} data-screen-label="Hero">
      <div style={ihStyles.heroInner}>
        <div style={ihStyles.heroKicker}>Intensive learning</div>
        <h1 style={ihStyles.heroH1}>What happens when you bite into a language <span style={{color:'var(--grey)'}}>(instead of nibbling)</span></h1>
        <p style={ihStyles.heroLead}>The brain (yes, yours too!) is truly remarkable. It adapts superbly to learning languages intensively – more intensively than most people would ever consider.</p>
        <p style={{...ihStyles.heroLead, marginTop:20}}>We've filmed ourselves pushing the limits in lots of different shapes – sprints and marathons, alone and in groups. It's challenging and messy but the results are extraordinary.</p>
      </div>
    </section>
  );
}

function IH_Shapes() {
  return (
    <section style={ihStyles.shapesWrap} data-screen-label="Two shapes">
      <div style={ihStyles.shapesInner}>
        <div style={ihStyles.kicker}>Two approaches that work</div>
        <h2 style={ihStyles.h2}>The sprint, and the steady burn</h2>
        <div style={ihStyles.shapesGrid} className="ih-shapes-grid">
          <div style={ihStyles.shapeRow}>
            <div style={ihStyles.shapeBig}>5–10 days</div>
            <div style={ihStyles.shapeSub}>at 8 hours a day. This is the approach we use with our celebrity learners. It's brutal in a wonderful way, and you come out the other side talking a new language (with a slightly melted brain).</div>
          </div>
          <div style={ihStyles.shapeRule} aria-hidden="true"/>
          <div style={ihStyles.shapeRow}>
            <div style={ihStyles.shapeBig}>An hour or two a day</div>
            <div style={ihStyles.shapeSub}>for a fixed run of weeks – you choose how many. This fits around real life, and if you're consistent, you'll be getting into your first conversations within a month.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- shared card grid used by sprints + marathons ----------
 *
 * Option B: bare YouTube embed, same as the Japanuary page. Click hits
 * YouTube's own play button inside its own iframe in one go – instant.
 * The brand tag and (if present) LIVE indicator live in the card body
 * below the video instead of overlaid on top.
 */

function IH_StoryCard({ story }) {
  const s = story;
  return (
    <div style={ihStyles.storyCard} className="ih-story-card">
      <div style={ihStyles.storyArtPlayer}>
        <iframe
          src={`https://www.youtube.com/embed/${s.videoId}?rel=0&modestbranding=1&playsinline=1`}
          title={s.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          loading="lazy"
          style={ihStyles.storyArtIframe}
        />
      </div>
      <div style={ihStyles.storyBody}>
        <div style={ihStyles.storyTagInline}>
          {s.live && (
            <span style={ihStyles.storyLiveInline}>
              <span style={ihStyles.storyLiveDot} aria-hidden="true"/>
              <span>Live now</span>
            </span>
          )}
          <span style={ihStyles.storyTagText}>{s.tag}</span>
        </div>
        <h3 style={ihStyles.storyTitle}><Link to={s.href} style={ihStyles.storyTitleA}>{s.title}</Link></h3>
        <p style={ihStyles.storySub}>{s.sub}</p>
        <ul style={ihStyles.storyMeta}>
          {s.meta.map((m, i) => <li key={i} style={ihStyles.storyMetaItem}>{m}</li>)}
        </ul>
        <Link to={s.href} style={ihStyles.storyMore}>Read the full story →</Link>
      </div>
    </div>
  );
}

function IH_StoryGrid({ kicker, h2, stories, columns=3 }) {
  return (
    <div style={ihStyles.storiesInner}>
      <div style={ihStyles.kicker}>{kicker}</div>
      <h2 style={ihStyles.h2}>{h2}</h2>
      <div style={{...ihStyles.storiesGrid, gridTemplateColumns:`repeat(${columns}, 1fr)`}} className="ih-stories-grid">
        {stories.map(s => (
          <IH_StoryCard key={s.slug} story={s}/>
        ))}
      </div>
    </div>
  );
}

function IH_Sprints() {
  const stories = [
    {
      slug: 'japanuary',
      tag: 'Sprint · January 2025',
      title: 'Japanuary.',
      sub: 'Ten days. Eight hours a day. None of us spoke Japanese on day one. Click below to see our messy but enthusiastic video diaries.',
      meta: ['10 days', 'Sprint format', 'Filmed in full'],
      href: '/intensive-japanuary',
      image: '/assets/high-intensity/japanuary/clips/EWS0YlmdGZg-max.jpg',
      videoId: 'EWS0YlmdGZg',
      live: false,
    },
    {
      slug: 'ireland',
      tag: 'Sprint · September 2025',
      title: 'Ten days of Irish.',
      sub: 'Carraroe, Connemara. A day-by-day video diary, a trip to the Aran Islands, and – on day ten – a radio interview, in Irish, with RTÉ Raidió na Gaeltachta.',
      meta: ['10 days', 'Carraroe', '16-video diary'],
      href: '/intensive-ireland',
      image: '/assets/high-intensity/ireland/clips/day-10-pt1-radio-interview.jpg',
      videoId: '3ERA8ctyYPg',
      live: false,
    },
    {
      slug: 'welsh-5day',
      tag: 'Sprint × 2 · 2017',
      title: 'Five days of Welsh.',
      sub: 'Two bootcamps in 2017. Six learners across two groups, on camera before they started – and again, in Welsh, five days later.',
      meta: ['5 days × 2', '6 learners', 'Before / after, on tape'],
      href: '/intensive-welsh-5day',
      image: 'https://i.ytimg.com/vi/UlRdBHzbHdY/hqdefault.jpg',
      videoId: 'UlRdBHzbHdY',
      live: false,
    },
    {
      slug: 'spanish-4day',
      tag: 'Sprint · 2014',
      title: 'Four days of Spanish.',
      sub: "The earliest filmed test of the method. Aran teaching Martyn – a Welsh tutor with zero Spanish – over four intensive days, ending with a Skype call to an Argentinean opera singer.",
      meta: ['2 + 2 days', '1 learner', 'Closing call on tape'],
      href: '/intensive-spanish-4day',
      image: '/assets/high-intensity/spanish-4days/01-aran-and-martyn.png',
      videoId: 'CdaLd5ytTAQ',
      live: false,
    },
  ];
  return (
    <section style={ihStyles.storiesWrap} data-screen-label="Sprints">
      <IH_StoryGrid
        kicker="Sprints · 5–10 days, all in"
        h2="The brain on full throttle."
        stories={stories}
        columns={2}
      />
    </section>
  );
}

function IH_Marathons() {
  const stories = [
    {
      slug: 'croatia',
      tag: 'Marathon · summer 2026',
      title: 'Aran’s 75 days.',
      sub: 'One hour of Croatian a day, livestreamed, ahead of a holiday in July 2026. Solo, unedited, every session in full – all 75 days complete.',
      meta: ['1 hour × 75 days', 'May 2 → July 15, 2026', 'Livestreamed daily'],
      href: '/intensive-croatia',
      image: '/assets/high-intensity/croatia/clips/aran-youtube-thumbnail.jpg',
      videoId: 'sqAChFizLiI',
      live: false,
    },
    {
      slug: 'welsh-tom',
      tag: 'Marathon · June 2021',
      title: 'Tom’s 30 days of Welsh.',
      sub: 'Up to two hours a day, for thirty days, starting from zero. No real-world deadline – just Tom testing whether the method he was working with actually worked on himself.',
      meta: ['Up to 2 hrs × 30 days', 'Complete beginner', '24 of 30 days on tape'],
      href: '/intensive-welsh-tom',
      image: 'https://i.ytimg.com/vi/BrL0OaUHALM/hqdefault.jpg',
      videoId: 'BrL0OaUHALM',
      live: false,
    },
  ];
  return (
    <section style={{...ihStyles.storiesWrap, background:'#fff', borderTop:'1px solid var(--border)'}} data-screen-label="Marathons">
      <IH_StoryGrid
        kicker="Marathons · weeks of daily practice"
        h2="The slow burn."
        stories={stories}
        columns={2}
      />
    </section>
  );
}
/* close marathons */

function IH_Advice() {
  const rules = [
    { n: '01', t: 'Pick a deadline.', p: 'A holiday, a film shoot, a wedding. Real-world deadlines beat made-up ones every time.' },
    { n: '02', t: 'Speak from day one.', p: 'Seriously, speak out loud in the gaps. Actually using your mouth recruits extra neurons to the memory formation.' },
    { n: '03', t: 'Brace for impact.', p: 'Intensive learning requires effort. That\u2019s how you buy the speed. It will feel out of control and roller-coastery.' },
    { n: '04', t: 'Be kind to yourself.', p: 'The bits you fumble are the bits your brain is learning. Treat them like good news.' },
  ];
  return (
    <section style={ihStyles.adviceWrap} data-screen-label="Advice">
      <div style={ihStyles.adviceInner}>
        <div style={ihStyles.kicker}>If you decide to try one</div>
        <h2 style={ihStyles.h2}>Four things we'd strongly recommend</h2>
        <ol style={ihStyles.rulesList} className="ih-rules-list">
          {rules.map(r => (
            <li key={r.n} style={ihStyles.ruleItem}>
              <div style={ihStyles.ruleN}>{r.n}</div>
              <div style={ihStyles.ruleT}>{r.t}</div>
              <p style={ihStyles.ruleP}>{r.p}</p>
            </li>
          ))}
        </ol>
        <p style={ihStyles.adviceCoda}>The longer version of all of this lives inside the three stories above, and on <a style={ihStyles.inlineA} href="/methodology">our methodology page</a>.</p>
      </div>
    </section>
  );
}

function IH_CTA() {
  const [coachOpen, setCoachOpen] = React.useState(false);
  return (
    <section style={ihStyles.ctaWrap} data-screen-label="CTA">
      <div style={ihStyles.ctaInner}>
        <div style={ihStyles.ctaKicker}>Two ways in</div>
        <h2 style={ihStyles.ctaH2}>Pick the one that fits</h2>
        <p style={ihStyles.ctaP}>Whether you want to try the method yourself or bring us in for a high-stakes preparation, we'd love to hear from you.</p>
        <div style={ihStyles.ctaGrid} className="ih-cta-grid">
          <div style={ihStyles.ctaCard}>
            <div style={ihStyles.ctaCardEyebrow}>Self-driven</div>
            <h3 style={ihStyles.ctaCardH3}>Test-drive it right now.</h3>
            <p style={ihStyles.ctaCardP}>No cost, no login – just jump straight in. Pick a language, hit play, and you'll know within the hour whether the method clicks.</p>
            <div style={ihStyles.ctaCardSpacer}/>
            <div style={ihStyles.ctaCardActions}>
              <a className="btn btn--primary" href="/#languages" style={ihStyles.ctaBtnLight}>Try a sample →</a>
              <a href="/pricing" style={ihStyles.ctaCardLink}>Or subscribe · £15/mo</a>
            </div>
          </div>
          <div style={{...ihStyles.ctaCard, ...ihStyles.ctaCardGold}}>
            <div style={{...ihStyles.ctaCardEyebrow, color:'rgba(255,255,255,.85)'}}>1:1 coaching with Aran</div>
            <h3 style={{...ihStyles.ctaCardH3, color:'#fff'}}>Talk to us about an intensive.</h3>
            <p style={{...ihStyles.ctaCardP, color:'rgba(255,255,255,.92)'}}>For TV productions, sports teams, business leaders, and anyone with a real deadline. Aran works with you directly. By application, limited slots.</p>
            <div style={ihStyles.ctaCardSpacer}/>
            <div style={ihStyles.ctaCardActions}>
              <button type="button" onClick={()=>setCoachOpen(true)} style={ihStyles.ctaBtnGold}>Talk to Aran →</button>
              <span style={{...ihStyles.ctaCardLink, color:'rgba(255,255,255,.75)'}}>Free initial session · no pitch</span>
            </div>
          </div>
        </div>
      </div>
      {coachOpen && <IH_CoachModal onClose={()=>setCoachOpen(false)}/>}
    </section>
  );
}

function IH_CoachModal({ onClose }) {
  const [sent, setSent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  const onSubmit = async e => {
    e.preventDefault();
    if (e.target.elements.website && e.target.elements.website.value) return;
    setSubmitting(true);
    setError(null);
    const data = Object.fromEntries(new FormData(e.target).entries());
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: 'intensive', ...data }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch {
      setError('Something went wrong — please try again or email us at hello@saysomethingin.com.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div style={ihCoachStyles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Talk to Aran about coaching">
      <div style={ihCoachStyles.card} onClick={e=>e.stopPropagation()}>
        <button type="button" onClick={onClose} style={ihCoachStyles.close} aria-label="Close">×</button>
        {sent ? (
          <div>
            <div style={ihCoachStyles.kicker}>Message sent.</div>
            <h3 style={ihCoachStyles.h3}>Thanks – Aran will be in touch within two working days.</h3>
            <p style={ihCoachStyles.p}>In the meantime, feel free to start the 7-day free trial and get a head start. If you end up booking coaching, we'll credit the first month's subscription back to you.</p>
            <div style={ihCoachStyles.actions}>
              <a className="btn btn--primary" href="/#languages" style={{...ihStyles.ctaBtnLight, background:'var(--brandRed)', color:'#fff'}}>Start 7 days free</a>
              <button type="button" onClick={onClose} style={ihCoachStyles.ghost}>Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            {/* Honeypot — invisible to real users, irresistible to bots. */}
            <div aria-hidden="true" style={{position:'absolute', left:'-10000px', top:'auto', width:1, height:1, overflow:'hidden'}}>
              <label>Website (leave this empty)<input type="text" name="website" tabIndex={-1} autoComplete="off"/></label>
            </div>
            <div style={ihCoachStyles.kicker}>1:1 coaching with Aran</div>
            <h3 style={ihCoachStyles.h3}>Tell us what you're preparing for.</h3>
            <p style={ihCoachStyles.p}>Intensive coaching only works well when there's a real personal connection. We'll be in touch to arrange a free initial training session with Aran – he'll focus on giving you a useful taste of the method itself, and we'll take it from there if it feels like a fit.</p>
            <p style={ihCoachStyles.p}>Aran is allergic to sales (which is a good thing, because he's genuinely atrocious at selling) so the call will be pure teaching. If Aran feels he could help you further, he'll say so; if not, he'll point you somewhere appropriate.</p>
            <div style={ihCoachStyles.fields}>
              <label style={ihCoachStyles.field}><span style={ihCoachStyles.label}>Your name</span><input name="name" required style={ihCoachStyles.input} placeholder="Ruth Jones"/></label>
              <label style={ihCoachStyles.field}><span style={ihCoachStyles.label}>Email</span><input name="email" required type="email" style={ihCoachStyles.input} placeholder="you@example.com"/></label>
              <label style={ihCoachStyles.field}><span style={ihCoachStyles.label}>What are you preparing for? And when is it?</span><textarea name="message" required rows={3} style={ihCoachStyles.textarea} placeholder="A filming day in French, a keynote in Mandarin, a trip to Madrid with family…"/></label>
              <label style={ihCoachStyles.field}><span style={ihCoachStyles.label}>Which language?</span><input name="topic" required style={ihCoachStyles.input} placeholder="Welsh, Spanish, etc."/></label>
            </div>
            {error && <p style={{...ihCoachStyles.fine, color:'var(--brandRed)'}}>{error}</p>}
            <div style={ihCoachStyles.actions}>
              <button type="button" onClick={onClose} style={ihCoachStyles.ghost}>Cancel</button>
              <button type="submit" disabled={submitting} className="btn btn--primary" style={{...ihStyles.ctaBtnLight, background:'var(--brandRed)', color:'#fff'}}>{submitting ? 'Sending…' : 'Send to Aran →'}</button>
            </div>
            <p style={ihCoachStyles.fine}>We only use your details to reply to this enquiry. No newsletter, no resale, no nonsense.</p>
          </form>
        )}
      </div>
    </div>
  );
}

const ihStyles = {
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(34px, 4vw, 48px)', lineHeight:1.06, letterSpacing:'-.012em', margin:'0 0 24px', textWrap:'balance' },
  inlineA: { color:'var(--brandRed)', fontWeight:600 },

  /* hero */
  heroWrap: { background:'var(--offWhite)', padding:'120px 32px 96px', borderBottom:'1px solid var(--border)' },
  heroInner: { maxWidth:880, margin:'0 auto' },
  heroKicker: { fontSize:13, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:22 },
  heroH1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(52px, 7.2vw, 92px)', lineHeight:1.04, letterSpacing:'-.028em', margin:'0 0 32px', textWrap:'balance' },
  heroLead: { fontFamily:'var(--font-display)', fontSize:'clamp(22px, 2.4vw, 28px)', lineHeight:1.35, color:'var(--fg-1)', margin:0, maxWidth:'42ch', textWrap:'balance' },

  /* shapes */
  shapesWrap: { background:'#fff', padding:'104px 32px 88px' },
  shapesInner: { maxWidth:1080, margin:'0 auto' },
  shapesGrid: { display:'grid', gridTemplateColumns:'1fr 1px 1fr', gap:48, alignItems:'start', marginTop:32 },
  shapeRow: {},
  shapeBig: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(40px, 5vw, 60px)', lineHeight:1, letterSpacing:'-.02em', color:'var(--brandRed)', marginBottom:14 },
  shapeSub: { fontSize:18, lineHeight:1.55, color:'var(--fg-1)', maxWidth:'40ch' },
  shapeRule: { width:1, background:'var(--border)', alignSelf:'stretch', minHeight:120 },
  shapesCoda: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'clamp(20px, 2vw, 24px)', color:'var(--grey)', marginTop:48, maxWidth:'52ch', textWrap:'balance' },

  /* stories */
  storiesWrap: { background:'var(--offWhite)', padding:'104px 32px 104px' },
  storiesInner: { maxWidth:1280, margin:'0 auto' },
  storiesGrid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24, marginTop:40 },
  storyCard: { background:'#fff', borderRadius:20, overflow:'hidden', textDecoration:'none', color:'inherit', display:'flex', flexDirection:'column', border:'1px solid var(--border)', transition:'transform 240ms cubic-bezier(.2,.7,.2,1), box-shadow 240ms cubic-bezier(.2,.7,.2,1)' },
  storyArt: { aspectRatio:'16 / 9', position:'relative', display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'20px 22px', color:'#fff', background:'#1a1a1a', overflow:'hidden', border:'none', cursor:'pointer', width:'100%', textAlign:'left', font:'inherit' },
  storyArtPlayer: { aspectRatio:'16 / 9', position:'relative', background:'#000', overflow:'hidden' },
  storyArtIframe: { position:'absolute', inset:0, width:'100%', height:'100%', border:0, display:'block' },
  storyArtYTOut: { position:'absolute', bottom:10, right:10, padding:'6px 10px', background:'rgba(0,0,0,.7)', color:'#fff', fontSize:11, fontWeight:600, letterSpacing:'.04em', textDecoration:'none', borderRadius:6, border:'1px solid rgba(255,255,255,.25)', backdropFilter:'blur(4px)', zIndex:2 },
  storyArtImg: { position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', display:'block' },
  storyArtScrim: { position:'absolute', inset:0, background:'linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.1) 38%, rgba(0,0,0,.1) 62%, rgba(0,0,0,.55) 100%)' },
  storyArtPlay: { position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:64, height:64, borderRadius:'50%', background:'rgba(255,255,255,.92)', color:'var(--brandRed)', fontSize:22, display:'flex', alignItems:'center', justifyContent:'center', paddingLeft:5, boxShadow:'0 10px 28px rgba(0,0,0,.35)', zIndex:2 },
  storyLive: { position:'relative', zIndex:3, alignSelf:'flex-end', display:'inline-flex', alignItems:'center', gap:8, padding:'6px 12px 6px 10px', background:'rgba(0,0,0,.55)', border:'1px solid rgba(255,255,255,.3)', borderRadius:999, fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700, backdropFilter:'blur(6px)' },
  storyLiveDot: { width:8, height:8, borderRadius:'50%', background:'var(--gold)', boxShadow:'0 0 0 3px rgba(254,201,2,.25)', animation:'ihPulse 1.6s ease-in-out infinite' },
  storyTag: { position:'relative', zIndex:3, fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700, color:'#fff', alignSelf:'flex-start', textShadow:'0 1px 4px rgba(0,0,0,.4)' },
  storyBody: { padding:'28px 28px 32px', display:'flex', flexDirection:'column', flex:1 },
  storyTitle: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(28px, 2.6vw, 34px)', lineHeight:1.1, letterSpacing:'-.015em', margin:'0 0 12px', color:'var(--fg-1)' },
  storyTitleA: { color:'inherit', textDecoration:'none' },
  storySub: { fontSize:16, lineHeight:1.55, color:'var(--fg-1)', margin:'0 0 18px' },
  storyMeta: { listStyle:'none', padding:0, margin:'0 0 22px', display:'flex', flexWrap:'wrap', gap:6 },
  storyMetaItem: { fontSize:12, color:'var(--grey)', background:'var(--offWhite)', border:'1px solid var(--border)', padding:'4px 10px', borderRadius:999 },
  storyMore: { marginTop:'auto', fontFamily:'var(--font-display)', fontWeight:700, fontSize:15, color:'var(--brandRed)' },
  /* Tag row that lives below the video instead of overlaid on top. */
  storyTagInline: { display:'flex', alignItems:'center', gap:10, marginBottom:12 },
  storyTagText: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700, color:'var(--grey)' },
  storyLiveInline: { display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px 3px 8px', background:'var(--fg-1)', color:'#fff', borderRadius:999, fontSize:10.5, letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700 },

  /* advice */
  adviceWrap: { background:'#fff', padding:'104px 32px 88px' },
  adviceInner: { maxWidth:1080, margin:'0 auto' },
  rulesList: { listStyle:'none', padding:0, margin:'32px 0 24px', display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:32 },
  ruleItem: { borderTop:'2px solid var(--brandRed)', paddingTop:18 },
  ruleN: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:14, color:'var(--brandRed)', letterSpacing:'.14em', marginBottom:12 },
  ruleT: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(20px, 2vw, 24px)', lineHeight:1.2, color:'var(--fg-1)', marginBottom:10, textWrap:'balance' },
  ruleP: { fontSize:15, lineHeight:1.55, color:'var(--fg-1)', margin:0 },
  adviceCoda: { fontSize:15, color:'var(--grey)', fontStyle:'italic', marginTop:8 },

  /* cta */
  ctaWrap: { background:'var(--brandRed)', color:'#fff', padding:'104px 32px 120px' },
  ctaInner: { maxWidth:760, margin:'0 auto', textAlign:'center' },
  ctaH2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(40px, 5vw, 64px)', lineHeight:1, letterSpacing:'-.022em', color:'#fff', margin:'0 auto 24px', textWrap:'balance', maxWidth:'18ch' },
  ctaP: { fontSize:18, lineHeight:1.65, color:'rgba(255,255,255,.92)', margin:'0 auto 48px', maxWidth:'52ch' },
  ctaKicker: { fontSize:12, letterSpacing:'.18em', textTransform:'uppercase', fontWeight:700, color:'var(--gold)', marginBottom:14 },
  ctaGrid: { display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:20, textAlign:'left', maxWidth:980, margin:'0 auto' },
  ctaCard: { background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.18)', borderRadius:18, padding:'32px 32px 28px', display:'flex', flexDirection:'column', minHeight:300 },
  ctaCardGold: { background:'rgba(254,201,2,.12)', border:'1px solid rgba(254,201,2,.42)' },
  ctaCardEyebrow: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', fontWeight:700, color:'rgba(255,255,255,.75)', marginBottom:12 },
  ctaCardH3: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(22px, 2.2vw, 28px)', lineHeight:1.15, letterSpacing:'-.01em', color:'#fff', margin:'0 0 12px' },
  ctaCardP: { fontSize:15, lineHeight:1.6, color:'rgba(255,255,255,.85)', margin:0 },
  ctaCardSpacer: { flex:1, minHeight:18 },
  ctaCardActions: { display:'flex', flexDirection:'column', gap:10, alignItems:'flex-start' },
  ctaBtnLight: { display:'inline-block', fontSize:'1rem', padding:'12px 26px 14px', background:'#fff', color:'var(--deepRed)', border:'none', textDecoration:'none' },
  ctaBtnGold: { fontSize:'1rem', padding:'12px 26px 14px', background:'var(--gold)', color:'#1a1004', border:'none', borderRadius:'999px', fontWeight:700, fontFamily:'inherit', cursor:'pointer' },
  ctaCardLink: { fontSize:13, color:'rgba(255,255,255,.7)', textDecoration:'none', borderBottom:'1px dotted rgba(255,255,255,.45)', paddingBottom:1 },
};

const ihCoachStyles = {
  backdrop: { position:'fixed', inset:0, background:'rgba(20,12,10,.72)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'24px' },
  card: { background:'#fff', borderRadius:18, maxWidth:560, width:'100%', maxHeight:'90vh', overflowY:'auto', padding:'40px 36px 32px', position:'relative', color:'var(--fg-1)', boxShadow:'0 30px 80px rgba(0,0,0,.4)' },
  close: { position:'absolute', top:14, right:18, width:36, height:36, borderRadius:'50%', background:'transparent', border:'1px solid var(--border)', color:'var(--grey)', fontSize:22, lineHeight:1, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  kicker: { fontSize:11, letterSpacing:'.18em', textTransform:'uppercase', fontWeight:700, color:'var(--brandRed)', marginBottom:10 },
  h3: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(24px, 2.4vw, 30px)', lineHeight:1.15, letterSpacing:'-.01em', margin:'0 0 14px', color:'var(--fg-1)' },
  p: { fontSize:15, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 14px' },
  fields: { display:'flex', flexDirection:'column', gap:14, margin:'22px 0 18px' },
  field: { display:'flex', flexDirection:'column', gap:6 },
  label: { fontSize:13, fontWeight:600, color:'var(--fg-1)' },
  input: { font:'inherit', fontSize:15, padding:'10px 12px', border:'1px solid var(--border-strong)', borderRadius:8, background:'#fff' },
  textarea: { font:'inherit', fontSize:15, padding:'10px 12px', border:'1px solid var(--border-strong)', borderRadius:8, background:'#fff', resize:'vertical' },
  actions: { display:'flex', gap:12, alignItems:'center', flexWrap:'wrap', marginTop:8 },
  ghost: { font:'inherit', fontSize:'1rem', padding:'11px 22px', background:'transparent', color:'var(--grey)', border:'1px solid var(--border-strong)', borderRadius:'999px', cursor:'pointer' },
  fine: { fontSize:12, color:'var(--grey)', marginTop:14 },
};

export { IH_Hero, IH_Shapes, IH_Sprints, IH_Marathons, IH_Advice, IH_CTA };
